/**
 * @ab/api-contract: request and response schemas shared by client and server
 * (ADR 0007). Every endpoint is described here once, as zod schemas, and both
 * sides import the types from this file.
 *
 * The round trip (ADR 0034, integration plan slice 7b): accounts and sessions,
 * runs with per-floor seeds and verified floor logs (ADR 0002), hatching as a
 * timed job with an idempotent claim (ADR 0003), and the test endpoints that
 * exist only on a server started with `AB_TEST_CLOCK=1`. Every path below is
 * under `/api` (the client calls it on its own origin; ADR 0034 D4).
 */
import { z } from 'zod';

/** Every response carries the server's idea of "now" (ADR 0003). */
export const serverNowSchema = z.object({ serverNow: z.iso.datetime() });
export type ServerNow = z.infer<typeof serverNowSchema>;

/** `GET /health` (and `GET /api/health`). */
export const healthResponseSchema = serverNowSchema.extend({ ok: z.literal(true) });
export type HealthResponse = z.infer<typeof healthResponseSchema>;

// ---------------------------------------------------------------- errors

/**
 * The closed set of refusals (ADR 0034). An error response is `{error, serverNow, requestId?}` with an HTTP status; the client
 * shows `ui.server.<code>`.
 */
export const errorCodes = [
  'bad_request',
  'bad_name',
  'bad_password',
  'name_taken',
  'bad_credentials',
  'unauthorized',
  'rate_limited',
  'not_found',
  'not_ready',
  'already_claimed',
  'invalid_log',
  'version_mismatch',
  'already_verified',
  'wrong_floor',
  'run_over',
  'no_egg',
  'slot_busy',
  'capacity_full',
  'conflict',
  // Slice 8b (ADR 0038): a staff action not allowed (on the staff member's own account, or by role); a staff token on a
  // play route; a locked account; creating an account without an invite code, or with one that is unknown or used.
  'forbidden',
  'staff_cannot_play',
  'account_locked',
  'invite_required',
  'bad_invite',
  // An account whose password is a temporary one from an admin (ADR 0038 D8): only `GET /api/me`, `PUT /api/me/password`
  // and logging out work until a new one is chosen.
  'must_change_password',
  // Slice 8c (ADR 0039): a claim whose items do not fit the airship's storage (the mail stays unclaimed); a speed-up
  // with too few skip tickets, or of a job already ready; a mail that is not the account's.
  'storage_full',
  'not_enough_tickets',
  'already_ready',
  'mail_not_found',
  // Slice 8e (ADR 0040 D4): a mail past its expiry (kept, never claimed). (8e's `plan_changed` is gone since slice 8h,
  // ADR 0042: the plan the player confirmed is the plan paid.)
  'expired',
  // Slice 8f (ADR 0041): a run started with a bag the rules or storage do not allow (`badBagReasons`, kept in
  // `rejections` as `detail.reason`; the client re-reads storage and says one line whatever the reason).
  'bad_bag',
  // Slice 8h (ADR 0042): a skip whose plan names a tier the rules lack, or whose tickets do not cover the time left.
  'bad_plan',
  // Slice 8h fix round (ADR 0042): a time zone this server's `Intl` does not resolve to a zone of its list or `UTC`
  // (registration and the staff's change; 400).
  'bad_time_zone',
] as const;
export const errorCodeSchema = z.enum(errorCodes);
export type ErrorCode = z.infer<typeof errorCodeSchema>;
/** `requestId`: the request's id (also its `X-Request-Id` header, ADR 0037 D5), so a player's report names the request. */
export const errorResponseSchema = serverNowSchema.extend({ error: errorCodeSchema, requestId: z.string().max(64).optional() });
export type ErrorResponse = z.infer<typeof errorResponseSchema>;
/**
 * What a client may have to show: a server's refusal, no answer at all (`offline`: the request never came back, or a
 * gateway in front of the API answered that it could not reach it), or a server error (`error`: the API failed on the
 * request, a 5xx without the contract's body).
 */
export type ShownError = ErrorCode | 'offline' | 'error';

/**
 * Text that may reach the database: no NUL character (Postgres's `text` and `jsonb` cannot hold one, so it would fail
 * the insert as a server error instead of being refused here).
 */
const noNul = (s: string): boolean => !s.includes('\u0000');
/** An idempotency key: a uuid or another URL-safe token of 8 to 64 characters. */
export const idempotencyKeySchema = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);
/**
 * A content id as a request names it (a dungeon, a species): lower-case letters, digits, `_` and `.`, at most 64
 * characters (content ids are `<type>.<name>`). The server still looks it up as an own key (`Object.hasOwn`).
 */
export const contentIdSchema = z.string().regex(/^[a-z0-9_.]{1,64}$/);

// ---------------------------------------------------------------- operations (ADR 0035 D8, D10)

/**
 * `GET /api/version`: what this API runs, so a deploy can confirm it (ADR 0035 D8): the build's git sha
 * (`AB_BUILD_SHA`, `-dirty` for a tree with changes, `dev` unbuilt), game-core's `coreVersion`, the content pack's hash
 * and the newest migration applied to its database (null on none). No secret.
 */
export const versionResponseSchema = serverNowSchema.extend({ build: z.string(), coreVersion: z.string(), contentHash: z.string(), migration: z.string().nullable() });
export type VersionResponse = z.infer<typeof versionResponseSchema>;

/**
 * `POST /api/client-errors` (ADR 0035 D10, ADR 0008: the client's errors tunnelled to our own server, no error SaaS):
 * an uncaught error or unhandled rejection of the page, answered 204 with no body. A token is optional (the account is
 * logged when one is given, never required).
 */
export const clientErrorSchema = z.object({
  message: z.string().min(1).max(1000).refine(noNul),
  stack: z.string().max(4000).refine(noNul).optional(),
  url: z.string().max(500).refine(noNul).optional(),
  build: z.string().max(64).refine(noNul).optional(),
  locale: z
    .string()
    .regex(/^[A-Za-z]{2,8}(-[A-Za-z0-9]{1,8}){0,3}$/)
    .optional(),
});
export type ClientError = z.infer<typeof clientErrorSchema>;

// ---------------------------------------------------------------- accounts and sessions (ADR 0034 D1, D2)

/** An account name: 3 to 24 letters, digits or `_`; unique without regard to case. */
export const accountNameSchema = z.string().regex(/^[A-Za-z0-9_]{3,24}$/);
/** A password: at least 8 characters (at most 128, so a hash never runs on megabytes). */
export const passwordSchema = z.string().min(8).max(128);

/** `POST /api/accounts` (create) and `POST /api/sessions` (log in): the body. The shapes are checked on create only. */
export const credentialsSchema = z.object({ name: z.string().max(64).refine(noNul), password: z.string().max(128).refine(noNul) });
export type Credentials = z.infer<typeof credentialsSchema>;

export const accountSchema = z.object({ id: z.uuid(), name: z.string(), createdAt: z.iso.datetime() });
export type Account = z.infer<typeof accountSchema>;

/**
 * An IANA time zone's name as a request carries it (slice 8h, ADR 0042; Q87): `Area/Location` words (`Asia/Tokyo`,
 * `America/Argentina/Buenos_Aires`, `Etc/GMT+5`) or `UTC`, at most 64 characters. The server accepts only a name its
 * `Intl` resolves to an entry of `Intl.supportedValuesOf('timeZone')` or `UTC`, and keeps that entry as the list spells
 * it (`asia/tokyo`, `Japan` → `Asia/Tokyo`); `bad_time_zone` otherwise (the slice 8h fix round).
 */
export const timeZoneSchema = z
  .string()
  .max(64)
  .regex(/^[A-Za-z_]+(\/[A-Za-z0-9_+-]+)*$/);

/**
 * `POST /api/accounts`: the name, the password and the invite code an admin issued (ADR 0038 D10): one account per
 * code. Without one the answer is `invite_required`; an unknown or used one is `bad_invite`. `timeZone` (slice 8h,
 * ADR 0042; Q87): the zone whose 05:00 begins the player's game day (`rules/timers.yaml` `gameDayStartsAt`), chosen
 * once; only staff change it later. Required: a body without it is `bad_request`; a zone the server does not know is
 * `bad_time_zone`.
 */
export const createAccountSchema = credentialsSchema.extend({ inviteCode: z.string().max(64).refine(noNul).optional(), timeZone: timeZoneSchema });
export type CreateAccount = z.infer<typeof createAccountSchema>;

/**
 * Both answer with a fresh bearer token (32 random bytes, base64url) and the account. `mustChangePassword`: the
 * password was a temporary one from an admin (ADR 0038 D8); the client asks for a new one before anything else
 * (`PUT /api/me/password`).
 */
export const sessionResponseSchema = serverNowSchema.extend({ token: z.string().min(43), account: accountSchema, mustChangePassword: z.boolean() });
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

/** `DELETE /api/sessions/current`: the token's session is gone. */
export const logoutResponseSchema = serverNowSchema.extend({ ok: z.literal(true) });

/**
 * `PUT /api/me/password`: the current password and the new one. It clears `mustChangePassword` and ends the account's
 * other sessions; a wrong current one is `bad_credentials`, a new one too short `bad_password`.
 */
export const changePasswordSchema = z.object({ current: z.string().max(128).refine(noNul), next: z.string().max(128).refine(noNul) });
export type ChangePassword = z.infer<typeof changePasswordSchema>;
export const changePasswordResponseSchema = serverNowSchema.extend({ ok: z.literal(true) });

// ---------------------------------------------------------------- the airship: eggs, the roster, timed jobs (ADR 0003, 0033)

/** A monster of the account's roster: a stable instance id, its species and level, when it hatched and from which job. */
export const rosterMonsterSchema = z.object({
  id: z.string(),
  species: z.string(),
  level: z.number().int().positive(),
  hatchedAt: z.iso.datetime(),
  job: z.uuid().nullable(),
});
export type RosterMonster = z.infer<typeof rosterMonsterSchema>;

/** An egg the account holds: aboard (placeable) or incubating (a job runs on it). A hatched egg is a roster monster. */
export const eggSchema = z.object({ id: z.uuid(), species: z.string(), status: z.enum(['aboard', 'incubating']), obtainedAt: z.iso.datetime() });
export type Egg = z.infer<typeof eggSchema>;

/** A skip ticket tier's id (`rules/timers.yaml` `skipTickets`): the client has no rules of its own, only these ids. */
export const ticketTierSchema = z.string().regex(/^[a-z0-9_]{1,32}$/);
/** Skip tickets by tier id. */
export const ticketCountsSchema = z.record(ticketTierSchema, z.number().int().nonnegative());
export type TicketCounts = z.infer<typeof ticketCountsSchema>;
/**
 * The tickets a skip spends (slice 8e, ADR 0040 D1; Q82): by tier, chosen by the server so their minutes add up to at
 * least the time left (the fewest minutes, then the fewest tickets, then the smaller tiers), from what the wallet holds
 * when `affordable`; when it is not, the plan an empty wallet would need. `minutes`: what the tickets cover.
 */
export const skipPlanSchema = z.object({ tickets: ticketCountsSchema, minutes: z.number().int().positive(), affordable: z.boolean() });
export type SkipPlan = z.infer<typeof skipPlanSchema>;

/** A timed job (ADR 0003), resolved at `serverNow`: `incubating` before `completesAt`, `ready` from it. Claimed jobs are not listed. */
export const timedJobSchema = z.object({
  id: z.uuid(),
  kind: z.literal('hatch'),
  /** The egg's species. */
  subject: z.string(),
  egg: z.uuid(),
  /** The incubator it runs in (an interactable id of the hatchery). */
  slot: z.string(),
  startedAt: z.iso.datetime(),
  completesAt: z.iso.datetime(),
  state: z.enum(['incubating', 'ready']),
  /** What skipping it costs at `serverNow` (slice 8e, ADR 0040 D1: a plan of ticket tiers); null once ready. */
  skipPrice: skipPlanSchema.nullable(),
  /**
   * The other plans a player may choose by hand (slice 8h, ADR 0042; Q88): one per tier the wallet holds whose tickets
   * alone cover the time left, as few of them as cover it, in the rules' order (shortest first); a plan equal to
   * `skipPrice` is left out. Empty once ready, or when no tier alone covers it.
   */
  skipOptions: z.array(skipPlanSchema),
});
export type TimedJob = z.infer<typeof timedJobSchema>;

/** A stack of the airship's storage (slice 8c, ADR 0039): an item and its count (one row per item). */
export const storageStackSchema = z.object({ item: z.string(), count: z.number().int().positive() });
export type StorageStack = z.infer<typeof storageStackSchema>;
/**
 * The account's wallet (ADR 0037 D6): what its balances hold. Skip tickets by tier (slice 8e): every tier of the
 * rules is present, a tier the wallet lacks as 0, in the rules' order (shortest first), so a client lists the tiers.
 */
export const walletSchema = z.object({ tickets: ticketCountsSchema });
export type Wallet = z.infer<typeof walletSchema>;

/** `GET /api/me`: the account and everything the airship holds, resolved at `serverNow`. */
export const meResponseSchema = serverNowSchema.extend({
  account: accountSchema,
  roster: z.array(rosterMonsterSchema),
  eggs: z.array(eggSchema),
  jobs: z.array(timedJobSchema),
  /** The password is a temporary one from an admin (ADR 0038 D8): the client asks for a new one first. */
  mustChangePassword: z.boolean(),
  /** The airship's storage, by item id (slice 8c): what a run's bag is packed from and comes back to. */
  storage: z.array(storageStackSchema),
  wallet: walletSchema,
  /** The zone the account's game day follows (slice 8h, ADR 0042): chosen when registering, changed only by staff. */
  timeZone: timeZoneSchema,
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/**
 * `POST /api/jobs/hatch`: puts an egg aboard in an incubator. A retry with the same key returns the same job; the same
 * key with another egg or slot is `conflict`.
 */
export const hatchRequestSchema = z.object({ eggId: z.uuid(), slot: z.string().regex(/^[a-z0-9_]{1,64}$/), idempotencyKey: idempotencyKeySchema });
export type HatchRequest = z.infer<typeof hatchRequestSchema>;
export const jobResponseSchema = serverNowSchema.extend({ job: timedJobSchema });
export type JobResponse = z.infer<typeof jobResponseSchema>;

/** `GET /api/jobs`: the unclaimed jobs, resolved at `serverNow`. */
export const jobsResponseSchema = serverNowSchema.extend({ jobs: z.array(timedJobSchema) });
export type JobsResponse = z.infer<typeof jobsResponseSchema>;

/**
 * `POST /api/jobs/:id/claim`: hatches a ready egg; a retry with the same key returns the stored result; a key used
 * before for another job is `conflict`.
 */
export const claimRequestSchema = z.object({ idempotencyKey: idempotencyKeySchema });
export type ClaimRequest = z.infer<typeof claimRequestSchema>;
export const claimResponseSchema = serverNowSchema.extend({ monster: rosterMonsterSchema });
export type ClaimResponse = z.infer<typeof claimResponseSchema>;

/**
 * `POST /api/jobs/:id/skip` (slice 8c, ADR 0038 D9, ADR 0039; slice 8e, ADR 0040 D1; slice 8h, ADR 0042): finishes an
 * incubating job now for the tickets of `plan`, the plan the player confirmed (the automatic `skipPrice.tickets`, one
 * of `skipOptions`, or any other): the server checks that its tiers are the rules' and that their minutes together
 * cover the time left at the request's time (`bad_plan` otherwise), and that the wallet holds them under its row locks
 * (`not_enough_tickets`, nothing spent); then it spends exactly that plan, waste included (`already_ready` once the
 * job's time has come). `price` is the plan paid. A retry with the same key gets the stored answer (a skip paid before
 * the tiers, slice 8c, answers `legacySkipPriceSchema`); the key used before for another job is `conflict`.
 *
 * Exactly one of `plan` and `auto` (the slice 8h fix round): `auto: true` is the game's own choice, planned by the
 * server under the wallet's locks at the request's time (the least waste; `not_enough_tickets` when the wallet cannot
 * cover it), so a confirmation left open while time passes never pays a plan that has gone stale; `plan` is a choice
 * by hand, spent exactly as above.
 */
export const skipRequestSchema = z
  .object({ idempotencyKey: idempotencyKeySchema, plan: ticketCountsSchema.refine((p) => Object.keys(p).length <= 16).optional(), auto: z.literal(true).optional() })
  .refine((r) => (r.plan === undefined) !== (r.auto === undefined), { message: 'exactly one of plan and auto' });
export type SkipRequest = z.infer<typeof skipRequestSchema>;
/**
 * A skip answered from a slice 8c record: it was paid in the old one-kind tickets (each a started hour), which have no
 * tier and covered no tier's minutes, so the answer names none (`legacy`) rather than make up a plan.
 */
export const legacySkipPriceSchema = z.object({ tickets: z.strictObject({}), minutes: z.literal(0), affordable: z.literal(true), legacy: z.literal(true) });
export type LegacySkipPrice = z.infer<typeof legacySkipPriceSchema>;
export const skipResponseSchema = serverNowSchema.extend({ job: timedJobSchema, price: z.union([skipPlanSchema, legacySkipPriceSchema]), wallet: walletSchema });
export type SkipResponse = z.infer<typeof skipResponseSchema>;

// ---------------------------------------------------------------- the mailbox (slice 8c, ADR 0039; ADR 0038 D6)

/**
 * The mails' templates: the closed list of `ui.mail.*` keys the server sends (each key the mail's title, `<key>_body`
 * its text; ADR 0017: never free text). `crew_*` are staff's ("From the crew", ADR 0038 D6); the gift and the bag's
 * overflow are the game's.
 */
export const mailTemplates = [
  'ui.mail.welcome',
  'ui.mail.overflow',
  'ui.mail.crew_egg',
  'ui.mail.crew_skip',
  'ui.mail.crew_password',
  'ui.mail.crew_tickets',
  'ui.mail.crew_gift',
  // Slice 8e (ADR 0040 D3): the rewards from play, "From the ship".
  'ui.mail.reward_login',
  'ui.mail.reward_hatch',
  'ui.mail.reward_clear',
  // Slice 8h (ADR 0042): staff changed the account's time zone (sent by that action only, never chosen in a crew mail).
  'ui.mail.crew_time_zone',
] as const;
export const mailTemplateSchema = z.enum(mailTemplates);
export type MailTemplate = z.infer<typeof mailTemplateSchema>;
/** The templates staff may send with `POST /api/admin/accounts/:id/mail`. */
export const crewMailTemplates = ['ui.mail.crew_egg', 'ui.mail.crew_skip', 'ui.mail.crew_password', 'ui.mail.crew_tickets', 'ui.mail.crew_gift'] as const;
/**
 * What a placeholder holds: `count`, a whole number 1..999; `species`, a monster's id; `item`, an item's id; since
 * slice 8e `tier`, a skip ticket tier's id (the rules'; shown by its name, `ui.ticket.<tier>`) and `dungeon`, a
 * dungeon's id; since slice 8h `time_zone`, a time zone's IANA name (`timeZoneSchema`, one the server's `Intl` knows;
 * shown as it is).
 */
export type MailParamKind = 'count' | 'species' | 'item' | 'tier' | 'dungeon' | 'time_zone';
/**
 * Each template's placeholders and what they hold (ADR 0017: typed templates): a mail carries exactly these, no
 * fewer and no others (the server checks the kinds against the content).
 */
export const mailTemplateParams = {
  'ui.mail.welcome': {},
  'ui.mail.overflow': {},
  'ui.mail.crew_egg': { species: 'species' },
  'ui.mail.crew_skip': {},
  'ui.mail.crew_password': {},
  'ui.mail.crew_tickets': { count: 'count', tier: 'tier' },
  'ui.mail.crew_gift': {},
  'ui.mail.reward_login': {},
  'ui.mail.reward_hatch': {},
  'ui.mail.reward_clear': { dungeon: 'dungeon' },
  'ui.mail.crew_time_zone': { zone: 'time_zone' },
} as const satisfies Readonly<Record<(typeof mailTemplates)[number], Readonly<Record<string, MailParamKind>>>>;
/**
 * A template's defaults (slice 8e, ADR 0040 D4; Q86): `pinned`, kept in the list once claimed however many mails come
 * after it. Nothing expires by default; an expiry is set per mail (by the system or staff).
 */
export const mailTemplateDefaults: Readonly<Partial<Record<(typeof mailTemplates)[number], { readonly pinned?: boolean }>>> = {
  'ui.mail.welcome': { pinned: true },
};
/**
 * Who a mail is from, as its list shows it: `crew`, staff's ("From the crew", ADR 0038 D6); `gift`, the new-player
 * gift; `ship`, the game's own (the bag's overflow, the rewards from play).
 */
export const mailSenders = ['crew', 'gift', 'ship'] as const;
export type MailSender = (typeof mailSenders)[number];
/**
 * A template's placeholders: a number, a content id the client names (`nameOf`), or (slice 8h) a time zone's name;
 * never free text. At most 8, named by lower-case words.
 */
export const mailParamsSchema = z
  .record(z.string().regex(/^[a-z_]{1,32}$/), z.union([z.number().int().min(0).max(1_000_000), contentIdSchema, timeZoneSchema]))
  .refine((p) => Object.keys(p).length <= 8);
export type MailParams = z.infer<typeof mailParamsSchema>;
/**
 * What a mail carries: items (into storage), an egg (aboard) or skip tickets of a tier (into the wallet, by postings;
 * the tier one of the rules', checked by the server; slice 8e).
 */
export const mailAttachmentSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('item'), item: contentIdSchema, count: z.number().int().min(1).max(999) }),
  z.strictObject({ kind: z.literal('egg'), species: contentIdSchema }),
  z.strictObject({ kind: z.literal('tickets'), tier: ticketTierSchema, count: z.number().int().min(1).max(100) }),
]);
export type MailAttachment = z.infer<typeof mailAttachmentSchema>;
export const mailAttachmentsSchema = z.array(mailAttachmentSchema).max(10);
/** What a claim granted: the stacks stowed, the eggs aboard (their ids), the tickets by tier (only the tiers it gave). */
export const mailGrantSchema = z.object({ items: z.array(storageStackSchema), eggs: z.array(z.object({ id: z.uuid(), species: z.string() })), tickets: ticketCountsSchema });
export type MailGrant = z.infer<typeof mailGrantSchema>;
export const mailSchema = z.object({
  id: z.uuid(),
  template: mailTemplateSchema,
  from: z.enum(mailSenders),
  params: mailParamsSchema,
  attachments: mailAttachmentsSchema,
  sentAt: z.iso.datetime(),
  readAt: z.iso.datetime().nullable(),
  claimedAt: z.iso.datetime().nullable(),
  /** Slice 8e (ADR 0040 D4): when an unclaimed mail stops being listed and claimable (null: never). */
  expiresAt: z.iso.datetime().nullable(),
  /** Kept in the list once claimed, however many mails come after it. */
  pinned: z.boolean(),
});
export type Mail = z.infer<typeof mailSchema>;
/**
 * `GET /api/mail` (slice 8e, ADR 0040 D4; Q86): every unclaimed mail not past its expiry (newest first), then the
 * pinned claimed ones, then the newest other claimed ones while the list holds fewer than 50 (the unclaimed and pinned
 * ones count against the 50 but are never left out); `unread`: how many listed were never opened. A mail past its
 * expiry is never listed (and never deleted).
 */
export const mailListResponseSchema = serverNowSchema.extend({ mails: z.array(mailSchema), unread: z.number().int().nonnegative() });
export type MailListResponse = z.infer<typeof mailListResponseSchema>;
/** `POST /api/mail/:id/read`: opened (once; again changes nothing). */
export const mailResponseSchema = serverNowSchema.extend({ mail: mailSchema });
export type MailResponse = z.infer<typeof mailResponseSchema>;
/**
 * `POST /api/mail/:id/claim {idempotencyKey}`: takes what it carries, once (conditional, ADR 0003): items into storage
 * (`storage_full` refuses the whole claim and leaves the mail unclaimed), an egg aboard, tickets into the wallet; a
 * mail past its expiry is `expired` (slice 8e) and stays as it is. A
 * retry with the same key gets the stored grant; another key on a claimed mail is `already_claimed`; the key used
 * before for another mail is `conflict`.
 */
export const mailClaimRequestSchema = z.object({ idempotencyKey: idempotencyKeySchema });
export const mailClaimResponseSchema = serverNowSchema.extend({ mail: mailSchema, granted: mailGrantSchema, wallet: walletSchema });
export type MailClaimResponse = z.infer<typeof mailClaimResponseSchema>;

// ---------------------------------------------------------------- runs (ADR 0002)

/**
 * A party member as game-core's `createRun` takes it (the shapes of `@ab/game-core`'s `PartyMember`, which this
 * package may not import): a human with its equipment, or a monster with its passive.
 */
export const partyMemberSchema = z.union([
  z.object({ monster: z.string(), level: z.number().int(), equipment: z.object({ weapon: z.string(), armor: z.string() }) }),
  z.object({ monster: z.string(), level: z.number().int(), passive: z.string() }),
]);
export type PartyMemberShape = z.infer<typeof partyMemberSchema>;
export const bagEntrySchema = z.object({ item: z.string(), count: z.number().int() });

/** A direction of `@ab/game-core`'s grid (`dirs`). */
export const dirSchema = z.enum(['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw']);
/** A skill, item or unit id inside a command: printable ASCII, at most 64 characters (so never a NUL). */
const commandIdSchema = z.string().regex(/^[\x21-\x7e]{1,64}$/);

/**
 * A command of a floor's log: `@ab/game-core`'s `Command` union as JSON, one strict shape per command type (the keys
 * game-core's `commandKeys` allows and nothing else): an unknown type or key is refused here, before any replay, and
 * never stored. `apply` still checks every value when the server replays the log (a skill the leader lacks, a target
 * out of reach). The server's tests keep this union and game-core's the same (`packages/server/test/contract.test.ts`).
 */
export const commandSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('wait') }),
  z.strictObject({ type: z.literal('enter_floor'), seed: z.number().int().nonnegative().max(0xffffffff) }),
  z.strictObject({ type: z.literal('turn'), dir: dirSchema }),
  z.strictObject({ type: z.literal('move'), dir: dirSchema }),
  z.strictObject({ type: z.literal('attack') }),
  z.strictObject({ type: z.literal('descend') }),
  z.strictObject({ type: z.literal('use_skill'), skill: commandIdSchema, dir: dirSchema.exactOptional(), target: commandIdSchema.exactOptional() }),
  z.strictObject({ type: z.literal('use_item'), item: commandIdSchema, dir: dirSchema.exactOptional(), target: commandIdSchema.exactOptional() }),
]);
export type CommandShape = z.infer<typeof commandSchema>;
/** A floor's log: at most 5000 commands. A log the contract refuses is `invalid_log` (400). */
export const floorLogSchema = z.array(commandSchema).max(5000);

/**
 * Why a chosen bag is refused (slice 8f, ADR 0041; `bad_bag`, 400): more stacks than `rules.dungeon.bagSlots`
 * (`too_many_stacks`), an item the content lacks (`unknown_item`), one a bag never takes (`not_usable`: not usable in
 * the dungeon and not a charm), an item twice (`duplicate`), a count above its `stackSize.dungeon` (`too_many`), more
 * than storage holds (`not_in_storage`). Checked in this order; the reason is kept in `rejections`, never answered.
 */
export const badBagReasons = ['too_many_stacks', 'unknown_item', 'not_usable', 'duplicate', 'too_many', 'not_in_storage'] as const;
export type BadBagReason = (typeof badBagReasons)[number];

/**
 * `POST /api/runs`: starts a run of a dungeon. The server builds the party, pins the versions and seeds floor 1.
 * `bag` (slice 8f, ADR 0041): the stacks the player packed, taken from storage (an empty list is an empty bag; see
 * `badBagReasons`); absent, the server packs it itself (`@ab/session` `autoBag`: `pnpm play --api` without `--bag`,
 * the bot, older clients). A count is a whole number from 1 to 999, at most 64 stacks: anything else is `bad_request`,
 * refused before the bag's rules are checked (so never a `bad_bag`, and not kept in `rejections`).
 */
export const startRunRequestSchema = z.object({
  dungeon: contentIdSchema,
  bag: z
    .array(z.strictObject({ item: contentIdSchema, count: z.number().int().min(1).max(999) }))
    .max(64)
    .optional(),
});
export type StartRunRequest = z.infer<typeof startRunRequestSchema>;

/** What a client needs to play a run: its pinned versions, its party and bag, and the current floor's seed. */
export const runSchema = z.object({
  runId: z.uuid(),
  dungeon: z.string(),
  /** The floor being played and its seed (a floor's seed is sent when the player enters it, never earlier: ADR 0002). */
  floor: z.number().int().positive(),
  seed: z.number().int().nonnegative(),
  coreVersion: z.string(),
  contentHash: z.string(),
  party: z.array(partyMemberSchema),
  bag: z.array(bagEntrySchema),
});
export type Run = z.infer<typeof runSchema>;
export const runResponseSchema = serverNowSchema.extend({ run: runSchema });
export type RunResponse = z.infer<typeof runResponseSchema>;

/**
 * `POST /api/runs/:id/floors/:n`: a finished floor's log (from its `enter_floor` to the command that left the floor or
 * ended the run), the state hash after it, and the versions the client played it with. The versions are kept as a
 * refusal's evidence when they do not match the run's, so they are bounded: 1–64 of `0-9 A-Z a-z . _ : + -` (a
 * semver core version, a hex content hash), never a NUL or a megabyte.
 */
const versionSchema = z.string().regex(/^[0-9A-Za-z._:+-]{1,64}$/);
export const floorSubmitSchema = z.object({ log: floorLogSchema, stateHash: z.string().regex(/^[0-9a-f]{64}$/), coreVersion: versionSchema, contentHash: versionSchema });
export type FloorSubmit = z.infer<typeof floorSubmitSchema>;

/** The verified floor's answer: the next floor's seed, or the run's end (with its egg, already among the account's eggs). */
export const floorResultSchema = z.union([
  z.object({ next: z.object({ floor: z.number().int().positive(), seed: z.number().int().nonnegative() }) }),
  z.object({ result: z.object({ status: z.enum(['completed', 'failed', 'escaped']), egg: z.object({ id: z.uuid(), species: z.string() }).optional() }) }),
]);
export type FloorResult = z.infer<typeof floorResultSchema>;
export const floorResponseSchema = serverNowSchema.and(floorResultSchema);
export type FloorResponse = z.infer<typeof floorResponseSchema>;

/**
 * `GET /api/runs/current`: the run still being played (none: null), with the logs of its verified floors, so a client
 * that lost its state rebuilds it by replaying them and enters the current floor with `seed` (the mid-run save).
 */
export const resumableRunSchema = runSchema.extend({ floors: z.array(z.object({ floor: z.number().int().positive(), seed: z.number().int().nonnegative(), log: floorLogSchema })) });
export type ResumableRun = z.infer<typeof resumableRunSchema>;
export const currentRunResponseSchema = serverNowSchema.extend({ run: resumableRunSchema.nullable() });
export type CurrentRunResponse = z.infer<typeof currentRunResponseSchema>;

/**
 * `POST /api/runs/:id/abandon`: the player gives the run up (the explicit give-up of a run this build cannot play,
 * ADR 0034). A run no longer being played is left as it is, with the same answer.
 */
export const abandonResponseSchema = serverNowSchema.extend({ ok: z.literal(true) });
export type AbandonResponse = z.infer<typeof abandonResponseSchema>;

// ---------------------------------------------------------------- test endpoints (only with AB_TEST_CLOCK=1; ADR 0034 D5)

/** `POST /api/test/clock`: sets the server's test clock or moves it forward. */
export const testClockRequestSchema = z.union([z.object({ set: z.iso.datetime() }), z.object({ advanceSeconds: z.number().int().nonnegative() })]);
/** `POST /api/test/seeds`: the next floor seeds the server hands out, in order (then random again); replaces any still queued. */
export const testSeedsRequestSchema = z.object({ seeds: z.array(z.number().int().nonnegative().max(0xffffffff)).max(100) });
/** `POST /api/test/eggs`: an egg of a species for the caller (a scenario builder over HTTP, for the browser tests). */
export const testEggRequestSchema = z.object({ species: contentIdSchema });
export const testOkResponseSchema = serverNowSchema.extend({ ok: z.literal(true) });
/**
 * `POST /api/test/staff`: an admin (ADR 0038) with a fresh name and password, and its TOTP secret (base32), so a test
 * logs in at `/api/admin/sessions` with a code it computes on the server's clock.
 */
export const testStaffResponseSchema = serverNowSchema.extend({ id: z.uuid(), name: z.string(), password: z.string(), secret: z.string() });
export type TestStaffResponse = z.infer<typeof testStaffResponseSchema>;
/**
 * `POST /api/test/storage {items}` (slice 8c): sets the caller's storage to these stacks (a scenario over HTTP: a full
 * storage for the browser tests); every item a known one, never equipment.
 */
export const testStorageRequestSchema = z.object({ items: z.array(z.object({ item: contentIdSchema, count: z.number().int().min(1).max(10_000) })).max(100) });
/** `POST /api/test/invites`: plain invite codes (ADR 0038 D10), each good for one account. */
export const testInvitesRequestSchema = z.object({ count: z.number().int().min(1).max(50) });
export const testInvitesResponseSchema = serverNowSchema.extend({ codes: z.array(z.string()) });
export type TestInvitesResponse = z.infer<typeof testInvitesResponseSchema>;
